import type {
  ConfidenceCheckerBriefAnalysis,
  ConfidenceCheckerSummaryEvaluationItem,
} from "@nlc/shared-types";

import { ChevronsDownUp, ChevronsUpDown, FileText } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/button";

import { ConfidenceCheckerBriefSection } from "./ConfidenceCheckerBriefSection";

interface ConfidenceCheckerBriefHistoryProps {
  briefAnalyses: ConfidenceCheckerBriefAnalysis[];
  evaluations: ConfidenceCheckerSummaryEvaluationItem[];
  onPreview?: (attachmentId: string) => void;
}

// Above this count, expanding every analysis by default would make the page
// too long to scan, so the "expand/collapse all" toggle earns its place.
const EXPAND_ALL_TOGGLE_THRESHOLD = 2;

export function ConfidenceCheckerBriefHistory({
  briefAnalyses,
  evaluations,
  onPreview,
}: ConfidenceCheckerBriefHistoryProps) {
  // Only the latest analysis starts open; lazy init so a refetch doesn't
  // re-collapse an analysis the user manually opened.
  const [openIds, setOpenIds] = useState<Set<number>>(
    () => new Set(briefAnalyses[0] ? [briefAnalyses[0].id] : []),
  );

  // A refetch can bring in a new analysis that becomes briefAnalyses[0];
  // auto-open it too, without touching anything the user already toggled.
  const latestId = briefAnalyses[0]?.id;
  const seenLatestId = useRef(latestId);

  useEffect(() => {
    if (latestId != null && latestId !== seenLatestId.current) {
      seenLatestId.current = latestId;
      setOpenIds((current) => new Set(current).add(latestId));
    }
  }, [latestId]);

  if (briefAnalyses.length === 0) {
    return (
      <div className="rounded-xl border border-border-secondary bg-bg-primary p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-lg bg-bg-secondary text-text-quaternary">
            <FileText size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              Brief Analysis
            </h2>
            <p className="text-sm text-text-tertiary">
              No AI brief analysis has been performed for this request yet.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const allOpen = briefAnalyses.every((analysis) => openIds.has(analysis.id));

  const setAnalysisOpen = (id: number, isOpen: boolean) => {
    setOpenIds((current) => {
      const next = new Set(current);

      if (isOpen) next.add(id);
      else next.delete(id);

      return next;
    });
  };

  const toggleAll = () => {
    setOpenIds(allOpen ? new Set() : new Set(briefAnalyses.map((a) => a.id)));
  };

  return (
    <div className="flex flex-col gap-4">
      {briefAnalyses.length > 1 && (
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl font-semibold text-text-primary">
            Brief Analyses ({briefAnalyses.length})
          </h2>
          {briefAnalyses.length > EXPAND_ALL_TOGGLE_THRESHOLD && (
            <Button
              color="gray"
              onPress={toggleAll}
              size="sm"
              variant="secondary"
            >
              {allOpen ? (
                <ChevronsDownUp size={14} />
              ) : (
                <ChevronsUpDown size={14} />
              )}
              {allOpen ? "Collapse all" : "Expand all"}
            </Button>
          )}
        </div>
      )}
      {briefAnalyses.map((briefAnalysis, index) => (
        <ConfidenceCheckerBriefSection
          briefAnalysis={briefAnalysis}
          evaluations={evaluations}
          isLatest={index === 0}
          isOpen={openIds.has(briefAnalysis.id)}
          key={briefAnalysis.id}
          onOpenChange={(isOpen) => setAnalysisOpen(briefAnalysis.id, isOpen)}
          onPreview={onPreview}
        />
      ))}
    </div>
  );
}
